import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { TrendingUp, RefreshCw, DollarSign, Users, Loader2, BarChart3, Wallet } from 'lucide-react'
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { useResource } from '@/shared/hooks/useResources'
import { useResourceStatistics } from '@/shared/hooks/useResourceStatistics'
import { useTelegram } from '@/shared/hooks/useTelegram'
import { formatToMoscowTime } from '@/shared/utils/format'
import type { Granularity, ResourceMetric, ResourceHourlyMetric } from '@/shared/types/statistics'

type TimeRange = '24h' | '7d' | '30d'
type MetricType = 'traffic' | 'revenue'

export default function ResourceStatistics() {
  const { resourceId } = useParams<{ resourceId: string }>()
  const navigate = useNavigate()
  const { hapticFeedback, hapticNotification, showBackButton, hideBackButton } = useTelegram()
  const { data: resource, isLoading: resourceLoading } = useResource(resourceId)

  const [timeRange, setTimeRange] = useState<TimeRange>('24h')
  const [metricType, setMetricType] = useState<MetricType>('traffic')
  const [forceRefresh, setForceRefresh] = useState(false)
  const [isTimeRangeChanging, setIsTimeRangeChanging] = useState(false)

  // Calculate date range and granularity based on selected time range
  const getDateRange = (range: TimeRange): { start: string; end: string; granularity: Granularity } => {
    const end = new Date()
    const start = new Date()

    switch (range) {
      case '24h':
        start.setDate(start.getDate() - 1)
        return {
          start: start.toISOString().split('T')[0],
          end: end.toISOString().split('T')[0],
          granularity: 'hour',
        }
      case '7d':
        start.setDate(start.getDate() - 7)
        return {
          start: start.toISOString().split('T')[0],
          end: end.toISOString().split('T')[0],
          granularity: 'day',
        }
      case '30d':
        start.setDate(start.getDate() - 30)
        return {
          start: start.toISOString().split('T')[0],
          end: end.toISOString().split('T')[0],
          granularity: 'day',
        }
    }
  }

  const dateRange = getDateRange(timeRange)
  const { data: stats, isLoading, error, refetch, isFetching } = useResourceStatistics(
    resourceId,
    dateRange.start,
    dateRange.end,
    dateRange.granularity,
    { enabled: true, refresh: forceRefresh }
  )

  // Setup BackButton
  useEffect(() => {
    window.scrollTo(0, 0)

    showBackButton(() => {
      window.scrollTo(0, 0)
      navigate(`/traffic/${resourceId}`)
    })

    document.body.style.backgroundColor = '#1e2832'

    return () => {
      hideBackButton()
      document.body.style.backgroundColor = ''
    }
  }, [showBackButton, hideBackButton, navigate, resourceId])

  // Transform time series data for charts
  const getChartData = () => {
    if (!stats?.time_series || stats.time_series.length === 0) return []

    return stats.time_series.map((item: ResourceMetric | ResourceHourlyMetric) => {
      // Determine if it's hourly or daily data
      const isHourly = 'metric_timestamp' in item
      const timeKey = isHourly ? item.metric_timestamp : (item as ResourceMetric).metric_date

      // Format time label in Moscow timezone
      const granularity = isHourly ? 'hour' : 'day'
      const timeLabel = formatToMoscowTime(timeKey, granularity)

      return {
        time: timeLabel,
        fullTime: timeKey,
        tasks: item.tasks_count,
        revenue: item.revenue_generated,
        completedTasks: item.completed_tasks,
        verifyingTasks: item.verifying_tasks,
        cancelledTasks: item.cancelled_tasks,
        activeTasks: (item.completed_tasks + item.verifying_tasks), // Line 1: active tasks
        completionRate: item.completion_rate,
      }
    })
  }

  // Calculate "X minutes ago" display from backend age_seconds
  const getTimeAgo = () => {
    if (!stats) return 'Нет данных'
    if (!stats.cache_metadata?.last_updated) return 'Только что'

    const ageSeconds = stats.cache_metadata.age_seconds

    if (ageSeconds < 60) return 'Только что'
    if (ageSeconds < 3600) return `${Math.floor(ageSeconds / 60)} мин назад`
    if (ageSeconds < 86400) return `${Math.floor(ageSeconds / 3600)} ч назад`
    return `${Math.floor(ageSeconds / 86400)} д назад`
  }

  // Manual refresh handler - forces backend cache bypass
  const handleRefresh = async () => {
    hapticFeedback('medium')
    setForceRefresh(true)
    try {
      await refetch()
      hapticNotification('success')
    } catch (err) {
      hapticNotification('error')
    } finally {
      // Reset refresh flag after fetch completes
      setForceRefresh(false)
    }
  }

  // Handle time range change
  const handleTimeRangeChange = (range: TimeRange) => {
    hapticFeedback('light')
    setIsTimeRangeChanging(true)
    setTimeRange(range)
  }

  // Reset loading state when data is loaded
  useEffect(() => {
    if (!isFetching && isTimeRangeChanging) {
      setIsTimeRangeChanging(false)
    }
  }, [isFetching, isTimeRangeChanging])

  // Handle metric type change
  const handleMetricTypeChange = (type: MetricType) => {
    hapticFeedback('light')
    setMetricType(type)
  }

  if (resourceLoading || !resource) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-tg-bg">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  const timeRanges = [
    { value: '24h' as const, label: '24ч', icon: '⏰' },
    { value: '7d' as const, label: '7д', icon: '📅' },
    { value: '30d' as const, label: '30д', icon: '📅' },
  ]

  const chartData = getChartData()
  const hasChartData = chartData.length > 0

  return (
    <div className="min-h-screen bg-tg-bg pb-24 pt-[96px]">
      {/* Header */}
      <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#059669]/10 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-[#059669]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-tg-text">Статистика</h1>
              <p className="text-sm text-tg-hint mt-1">{resource.name}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-3 py-4 space-y-3">
        {/* Time Range Selector */}
        <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden p-3">
          <div className="flex gap-1 p-1 bg-tg-bg rounded-xl">
            {timeRanges.map((range) => (
              <button
                key={range.value}
                onClick={() => handleTimeRangeChange(range.value)}
                className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition-all ${
                  timeRange === range.value
                    ? 'bg-tg-button text-white shadow-lg'
                    : 'text-tg-hint hover:text-tg-text'
                }`}
              >
                <span className="text-base mr-1">{range.icon}</span>
                {range.label}
              </button>
            ))}
          </div>
        </div>

        {/* Refresh Section */}
        <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-tg-text">Последнее обновление</p>
              <p className="text-xs text-tg-hint mt-1">
                {getTimeAgo()}
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={isFetching}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                isFetching
                  ? 'bg-tg-hint/20 text-tg-hint cursor-not-allowed'
                  : 'bg-tg-button text-white hover:shadow-lg active:scale-95'
              }`}
            >
              <RefreshCw
                className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`}
              />
              {isFetching ? 'Загрузка...' : 'Обновить'}
            </button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <div className="text-4xl mb-3">❌</div>
            <h3 className="text-lg font-semibold text-red-900 mb-2">Не удалось загрузить статистику</h3>
            <p className="text-sm text-red-600 mb-4">{error.message}</p>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Повторить
            </button>
          </div>
        )}

        {/* Loading Skeleton */}
        {isLoading && (
          <div className="grid grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="bg-tg-secondary-bg rounded-2xl p-4 h-32 animate-pulse"
              />
            ))}
          </div>
        )}

        {/* Summary Cards - Only 2 cards as requested */}
        {stats?.summary && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {/* Card 1: Expense/Consumption */}
              <div className="bg-tg-secondary-bg rounded-2xl p-3 shadow-sm border-2 border-[#ef4444]/20">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-[#ef4444]/10 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-[#ef4444]" />
                  </div>
                  <span className="text-xs text-tg-hint">
                    {resource.resource_type === 'bot' ? 'Потребление' : 'Расход'}
                  </span>
                </div>
                <div className="text-xl font-bold text-tg-text">
                  {stats.summary.total_revenue.toFixed(2)} ₽
                </div>
                <div className="text-xs text-tg-hint mt-0.5">
                  {resource.resource_type === 'boost'
                    ? 'включая ежедневные списания'
                    : `${stats.summary.avg_daily_revenue.toFixed(2)} ₽/день`}
                </div>
              </div>

              {/* Card 2: Subscribers/Boosts/Users Count */}
              <div className="bg-tg-secondary-bg rounded-2xl p-3 shadow-sm border-2 border-[#059669]/20">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-[#059669]/10 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#059669]" />
                  </div>
                  <span className="text-xs text-tg-hint">
                    {resource.resource_type === 'channel' || resource.resource_type === 'group' ? 'Подписчики' :
                     resource.resource_type === 'boost' ? 'Количество бустов' : 'Количество пользователей'}
                  </span>
                </div>
                <div className="text-xl font-bold text-tg-text">
                  {stats.summary.total_active}
                </div>
                <div className="text-xs text-tg-hint mt-0.5">
                  {stats.summary.pending_verification} в ожидании проверки
                </div>
              </div>
            </div>

          </div>
        )}

        {/* Chart Section */}
        {stats && hasChartData && (
          <>
            {/* Metric Type Toggle */}
            <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden p-3">
              <div className="flex gap-2">
                <button
                  onClick={() => handleMetricTypeChange('traffic')}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2 ${
                    metricType === 'traffic'
                      ? 'bg-[#3b82f6] text-white shadow-lg'
                      : 'bg-tg-bg text-tg-hint hover:text-tg-text'
                  }`}
                >
                  <BarChart3 className="w-4 h-4" />
                  Трафик
                </button>
                <button
                  onClick={() => handleMetricTypeChange('revenue')}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2 ${
                    metricType === 'revenue'
                      ? 'bg-[#059669] text-white shadow-lg'
                      : 'bg-tg-bg text-tg-hint hover:text-tg-text'
                  }`}
                >
                  <Wallet className="w-4 h-4" />
                  Расход
                </button>
              </div>
            </div>

            {/* Chart Container */}
            {isTimeRangeChanging ? (
              <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden p-3">
                <div className="h-[440px] animate-pulse flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
                </div>
              </div>
            ) : (
              <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden p-3">
                <h3 className="text-sm font-semibold text-tg-text mb-2 px-1">
                  {metricType === 'traffic' ? 'График трафика' : 'График расходов'}
                </h3>

                <div className="w-full h-[400px] -ml-2">
                  <ResponsiveContainer width="100%" height="100%" key={`${timeRange}-${metricType}`}>
                  {metricType === 'traffic' ? (
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2a3441" />
                      <XAxis
                        dataKey="time"
                        stroke="#6b7280"
                        tick={{ fill: '#9ca3af', fontSize: 12 }}
                      />
                      <YAxis
                        stroke="#6b7280"
                        tick={{ fill: '#9ca3af', fontSize: 11 }}
                        width={40}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1e2832',
                          border: '1px solid #374151',
                          borderRadius: '8px',
                          color: '#f9fafb',
                        }}
                        labelStyle={{ color: '#9ca3af' }}
                      />
                      <Legend
                        wrapperStyle={{ color: '#9ca3af' }}
                        iconType="line"
                      />
                      <Line
                        type="monotone"
                        dataKey="activeTasks"
                        stroke="#059669"
                        strokeWidth={2}
                        name={
                          resource.resource_type === 'channel' || resource.resource_type === 'group' ? 'Активные подписчики' :
                          resource.resource_type === 'boost' ? 'Активные бусты' : 'Активные пользователи'
                        }
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="cancelledTasks"
                        stroke="#ef4444"
                        strokeWidth={2}
                        name="Отмененные"
                        dot={false}
                      />
                    </LineChart>
                  ) : (
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#059669" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2a3441" />
                      <XAxis
                        dataKey="time"
                        stroke="#6b7280"
                        tick={{ fill: '#9ca3af', fontSize: 12 }}
                      />
                      <YAxis
                        stroke="#6b7280"
                        tick={{ fill: '#9ca3af', fontSize: 11 }}
                        width={40}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1e2832',
                          border: '1px solid #374151',
                          borderRadius: '8px',
                          color: '#f9fafb',
                        }}
                        labelStyle={{ color: '#9ca3af' }}
                        formatter={(value: number) => `${value.toFixed(2)} ₽`}
                      />
                      <Legend
                        wrapperStyle={{ color: '#9ca3af' }}
                      />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        stroke="#059669"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#revenueGradient)"
                        name="Расход (₽)"
                      />
                    </AreaChart>
                  )}
                </ResponsiveContainer>
              </div>
            </div>
            )}
          </>
        )}

        {/* Empty State */}
        {!stats && !isLoading && !error && (
          <div className="bg-tg-secondary-bg rounded-2xl p-8 text-center">
            <div className="text-6xl mb-4">📊</div>
            <h3 className="text-lg font-semibold text-tg-text mb-2">Нет данных</h3>
            <p className="text-sm text-tg-hint mb-4">
              За выбранный период нет данных для отображения
            </p>
          </div>
        )}

        {/* No Data State for Chart */}
        {stats && !hasChartData && (
          <div className="bg-tg-secondary-bg rounded-2xl p-8 text-center">
            <div className="text-4xl mb-3">📉</div>
            <h3 className="text-lg font-semibold text-tg-text mb-2">Нет данных</h3>
            <p className="text-sm text-tg-hint">
              За выбранный период нет данных для отображения
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
